import {useEffect, useState} from "react";
import MimeTypes from "../../models/MimeTypes";
import ImageUtils from "../../utils/ImageUtils";
import ValuerAmendFilePreview from "./ValuerAmendFilePreview";
import cloud from "../../assets/upload_cloud.png";
import ValuerAmendFreeTextInput from "./ValuerAmendFreeTextInput";
import valuer_api from "../../api/valuer";
import doc from "../../assets/account_circle.png";


const ValuerAmendAppendicesContainer = (props) => {

    // For Saving
    const summaryId = props.summaryId
    const section = props.sectionName
    const identifier = props.identifier

    // Required
    const field = props.field

    // Optional
    const notes = props.notes

    // Value
    const [newFileName, setNewFileName] = useState('')
    const [newFileBase64, setNewFileBase64] = useState('')

    const [files, setFiles] = useState([]);

    // File Preview Size
    const fileWidth = props.fileWidth
    const fileHeight = props.fileHeight

    // Saving Tracker
    const UpdateParentValue = props.UpdateParentValue
    const AddToQueue = props.AddToQueue
    const RemoveFromQueue = props.RemoveFromQueue
    const AddToErroredSaves = props.AddToErroredSaves

    // Error Handling
    const setErrorMessage = props.setErrorMessage
    const setErrorTitle = props.setErrorTitle


    useEffect(() => {
        if (!summaryId || !section || !identifier || !props.values) return
        GetFiles()
    }, [summaryId, section, identifier, props.values]);

    const GetFiles = () => {
        let response = valuer_api.ValuerAmendFileGet(summaryId, identifier, `${identifier}-item`);
        response.then(res => {
            let _fi = res.data.files || [];
            for (let i = 0; i < _fi.length; i++) {
                const f = _fi[i];
                const file_type = (f.filename || "").split(".").at(-1);
                const MIME_TYPE = MimeTypes[file_type];
                const FILE_DATA = f.file_data || "";
                _fi[i].base64 = FILE_DATA && MIME_TYPE ? `data:${MIME_TYPE};base64,${FILE_DATA}` : (f.base64 || "");

                const existing = files.find(x => String(x.row ?? x.id) === String(f.row ?? f.file_row ?? f.id));
                const localTitle = existing ? existing.title : "";
                const backendTitle =
                    (props.values && props.values[f.id] ? props.values[f.id][`${identifier}-item`] : (f.title || ""));

                _fi[i].title = backendTitle || localTitle || "";

                // *** keep a row we can round-trip back ***
                _fi[i].row = String(f.row ?? f.file_row ?? f.id ?? "NO_ROW");
            }
            setFiles(_fi);
        }).catch((err) => {
            console.log(err);
            setErrorTitle("Failed to Get Files");
            setErrorMessage("The request to get the requests files has failed");
        });
    };


    const AddNewFile = (file_object) => {
        const updateTime = Date.now();
        AddToQueue(section, identifier, updateTime);

        const mappedExisting = files.map(f => ({
            filename: f.filename,
            base64: f.base64,
            title: f.title || "",
            row: String(f.row ?? f.id ?? "NO_ROW")
        }));
        const newOne = {
            filename: file_object.filename,
            base64: file_object.base64,
            title: file_object.title || "",
            row: "NO_ROW"
        };

        const body = { files: [...mappedExisting, newOne] };

        valuer_api.ValuerAmendFilePut(summaryId, identifier, `${identifier}-item`, body)
            .then(() => {
                RemoveFromQueue(section, identifier, updateTime);
                GetFiles();
            })
            .catch(() => {
                AddToErroredSaves(section, identifier, updateTime);
                setErrorTitle("Failed to Save Files");
                setErrorMessage("The request to save the files has failed");
            });
    };

    const deleteFile = (file_index) => {
        const updateTime = Date.now();
        AddToQueue(section, identifier, updateTime);

        const remaining = files
            .filter((_, i) => i !== file_index)
            .map(f => ({
                filename: f.filename,
                base64: f.base64,
                title: f.title || "",
                row: String(f.row ?? f.id ?? "NO_ROW")
            }));

        const body = { files: remaining };

        valuer_api.ValuerAmendFilePut(summaryId, identifier, `${identifier}-item`, body)
            .then(() => {
                RemoveFromQueue(section, identifier, updateTime);
                GetFiles();
            })
            .catch(() => {
                AddToErroredSaves(section, identifier, updateTime);
                setErrorTitle("Failed to Delete File");
                setErrorMessage("The request to delete the file has failed");
            });
    };

    const handlePaste = async (event) => {
        try {
            if (!navigator.clipboard) {
                // Clipboard API is restricted to HTTPS websites
                setErrorTitle("Clipboard Error")
                setErrorMessage("The Clipboard API is unavailable")
                return;
            }
            const clipboardItems = await navigator.clipboard.read();
            // Iterate over clipboard items until you find the first image file
            for (let clipboardItem of clipboardItems) {
                let imageTypes = clipboardItem.types.find(type => type.startsWith('image/'));
                if (!imageTypes) continue;
                const blob = await clipboardItem.getType(imageTypes);

                // Generate a file name and format
                let ts = Date.now()
                let fileEnding
                for (const [key, value] of Object.entries(MimeTypes))
                    if (value === imageTypes) fileEnding = "." + key
                if (!fileEnding) {
                    setErrorTitle("File Error")
                    setErrorMessage("The given file type is not supported")
                    break;
                }
                let filename = "paste_" + ts + fileEnding
                setNewFileName(filename)

                // Get Base64 From BLOB
                let base64_image = await ImageUtils.convertBase64(blob);
                setNewFileBase64(base64_image)

                // Only check the first image file
                break;
            }
        } catch (err) {
            setErrorTitle("File Error")
            setErrorMessage("There was an error reading the image from your clipboard")
        }
    };

    const handleFileUploaderClick = () => {
        let element = document.getElementById("file-uploader-" + identifier)
        if (!element) return;
        element.click()
    }

    const handleDrop = async (ev) => {
        ev.preventDefault()
        let _files = ev.dataTransfer.files
        for (let i=0; i<_files.length; i++) {
            let _file = _files[i]
            let base64 = await ImageUtils.convertBase64(_file);
            let _file_object = { "filename": _file.name, "base64": base64, "title": ""}
            AddNewFile(_file_object)
        }
    }

    const handleSelect = async (ev) => {
        for (let i=0; i < ev.target.files.length; i++) {
            let file = ev.target.files[i]
            let base64 = await ImageUtils.convertBase64(file);
            let _file_object = {"filename": file.name, "base64": base64, "title": ""}
            AddNewFile(_file_object)
            //Reset input file list so that it doesn't fail when uploading a previously uploaded file
            ev.target.value = null;
        }
    }

    const handleUploadClipboard = async (ev) => {
        if (!newFileName || !newFileBase64) return;
        let _file_object = {
            "filename": newFileName,
            "base64": newFileBase64,
            "title": ""
        }
        AddNewFile(_file_object)
    }

    const UpdateValues = (file_id) => {
        let file_idx = files.map(x => x.row).indexOf(file_id)


        if (file_idx === -1) return
        let updateTime = Date.now()
        AddToQueue(section, identifier, updateTime)
        let body = {
            "value": files[file_idx].title,
            "row": file_id,
        }
        let response = valuer_api.ValuerAmendPut(summaryId, identifier, `${identifier}-item`, body);
        response.then(() => {
            RemoveFromQueue(section, identifier, updateTime)
        }).catch(() => {
            AddToErroredSaves(section, identifier, updateTime)
            setErrorTitle("Failed to Save")
            setErrorMessage("The request to save a field has failed")
        })
    }

    const ClickOff = (file_id) => {
        // Check for required fields and then submit change
        if (!summaryId || !section || !identifier || !file_id) return
        UpdateValues(file_id)
    }

    return (
        <div className="valuer-amend-file-field-container">
            {field && field
                ? <div className="valuer-amend-field-label"> {field} </div>
                : null
            }
            <div className={"valuer-amend-file-upload-fields-container "}>
                <div className="valuer-amend-file-upload-paste-container">
                    <textarea placeholder={"paste screenshot here"} className="valuer-amend-field image-paste"
                              onPaste={handlePaste} value={newFileName}/>
                    <div className="valuer-amend-fil-upload-paste-button link"
                         onClick={() => handleUploadClipboard()}>
                        >
                    </div>
                </div>
                <div className="valuer-amend-file-upload-separator-text"> or</div>
                <div className="valuer-amend-file-upload-drag-container">
                    <div className="valuer-amend-file-upload-valuer-dropzone"
                         onDragOver={(ev) => ev.preventDefault()}
                         onDrop={handleDrop}>
                        <img src={cloud} className="valuer-amend-file-upload-valuer-dropzone-cloud"
                             alt="upload cloud"/>
                        <div className="valuer-amend-file-upload-valuer-dropzone-text">
                            Drag and drop files here or <a className="valuer-amend-file-uploader-opener link"
                                                           onClick={() => handleFileUploaderClick()}> select a
                            file</a>
                        </div>
                        <input type='file' id={"file-uploader-" + identifier} multiple={true}
                               className="file-uploader-hidden" onChange={handleSelect}/>
                    </div>
                </div>
            </div>
            <div className="file-amend-file-upload-notes-container">
                {notes && notes.map((note) => {
                    return (
                        <div className="file-amend-file-upload-note">
                            {note}
                        </div>
                    )
                })}
            </div>
            <div className="valuer-amend-appendices-list-preview-container">
                {files && files.map((file, idx) => {
                    let _temp_base64 = file.base64
                    let _position = _temp_base64.search("data:image");
                    if (_position === -1) _temp_base64 = doc
                    return (
                        <div className="valuer-amend-appendices-row-container">
                            <div className="valuer-amend-appendices-file-preview-container">
                                <ValuerAmendFilePreview filename={file.filename}
                                                        file_base64={_temp_base64}
                                                        fileWidth={fileWidth}
                                                        fileHeight={fileHeight}
                                                        setErrorMessage={setErrorMessage}
                                                        setErrorTitle={setErrorTitle}
                                                        deleteActive={true}
                                                        deleteFunction={deleteFile.bind(this)}
                                                        fileIndex={idx}
                                />
                            </div>
                            <div className="valuer-amend-appendices-title-container">
                                <div className="valuer-amend-field-label"> Appendices title </div>
                                <div className="valuer-amend-field-input-container"
                                     style={{width: "100%", height: "48px", minHeight: "48px", minWidth: "100%"}}>
                                    <textarea
                                        value={file.title}
                                        className="valuer-amend-field"
                                        onChange={(ev) => {
                                            const v = ev.target.value;
                                            setFiles(prev => {
                                                const copy = [...prev];
                                                copy[idx] = { ...copy[idx], title: v };
                                                return copy;
                                            });
                                        }}
                                        onBlur={() => ClickOff(file.row)}
                                    />

                                </div>
                                <div className="valuer-amend-free-text-counter-container">
                                    {file.title?.length ? file.title?.length : "0" + "/1024"}
                                </div>
                            </div>
                        </div>

                    )
                })}
            </div>


        </div>
    )
}


export default ValuerAmendAppendicesContainer;